import { makeObservable, observable, runInAction, action } from 'mobx';
import { AccountingStatementInputDto } from '../../types';
import { InputBaseModel, NodeBaseModel } from '..';
import { TypeUtils } from '../../../common/misc';
import { SourceModelBase, StaticSourceModel } from '../../../custom_shared/models';
import { SourceProvider } from '../../../custom_shared/misc';
import { Filter } from '../../../common/services/SessionService';
import { SourceFetchConditionValidator } from '../../misc';

interface FetchSourceOptionsParams {
    ignoreCache?: boolean;
}

export default class InputModelWithSource extends InputBaseModel {
    sourceProvider: SourceProvider;
    hasSourceOptionsLoaded: boolean = false;

    // Ensure smooth experience by waiting for a minimum loading time
    private loadingStartTime: number | null = null;
    private loadingTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private readonly minimumLoadingTime = 500;

    constructor(node: NodeBaseModel, data: AccountingStatementInputDto, source?: SourceModelBase) {
        super(node, data);

        this.createSourceProvider(source);

        makeObservable(this, {
            sourceProvider: observable,
            hasSourceOptionsLoaded: observable,
            setHasSourceOptionsLoaded: action
        });
    }

    setHasSourceOptionsLoaded(hasSourceOptionsLoaded: boolean) {
        this.hasSourceOptionsLoaded = hasSourceOptionsLoaded;
    }

    loadAndSyncSourceOptions(): void {
        if (this.sourceProvider.loading) {
            return;
        }

        if (!this.hasSourceOptionsLoaded) {
            this.fetchSourceOptions();
        } else {
            this.syncValueWithOptions();
        }
    }

    async fetchSourceOptions({ ignoreCache }: FetchSourceOptionsParams = {}) {
        try {
            this.startLoading();
            await this.sourceProvider.getSourceOptions(ignoreCache, this.getSourceFetchFilter());
            this.syncValueWithOptions();
            this.setHasSourceOptionsLoaded(true);
        } catch (e) {
            console.error(`Error while fetching source options for input '${this.id} ${this.guid}'.`, e);
        } finally {
            await this.stopLoading();
        }
    }

    private startLoading(): void {
        if (this.sourceProvider.isLinkedSource) {
            this.cleanupLoadingTimeout();
            this.loadingStartTime = Date.now();
            this.sourceProvider.setLoading(true);
        }
    }

    private async stopLoading(): Promise<void> {
        if (this.sourceProvider.isLinkedSource) {
            await this.ensureMinimumLoadingTime();
            this.loadingStartTime = null;
            this.sourceProvider.setLoading(false);
        }
    }

    private async ensureMinimumLoadingTime(): Promise<void> {
        if (!this.loadingStartTime) {
            return;
        }

        const elapsedTime = Date.now() - this.loadingStartTime;
        const remainingTime = Math.max(0, this.minimumLoadingTime - elapsedTime);

        if (remainingTime === 0) {
            return;
        }

        await new Promise(resolve => {
            this.loadingTimeoutId = setTimeout(resolve, remainingTime);
        });

        this.cleanupLoadingTimeout();
    }

    private cleanupLoadingTimeout(): void {
        if (this.loadingTimeoutId) {
            clearTimeout(this.loadingTimeoutId);
            this.loadingTimeoutId = null;
        }
    }

    private syncValueWithOptions(): void {
        if (TypeUtils.isStringArray(this.value)) {
            return this.setValue(this.value.filter(v => this.sourceProvider.values.includes(v)));
        }

        if (TypeUtils.isString(this.value) && !this.sourceProvider.values.includes(this.value)) {
            return this.setValue(null);
        }
    }

    private createSourceProvider(source?: SourceModelBase) {
        runInAction(() => {
            if (source) {
                this.sourceProvider = new SourceProvider(source);
                return;
            }

            console.warn(`Source not found for input '${this.id} ${this.guid}'`);
            this.sourceProvider = new SourceProvider(new StaticSourceModel({ id: '', type: '', link: null, data: [] }));
        });
    }

    private getSourceFetchFilter(): Filter | undefined {
        if (!this.sourceFetchCondition) {
            return undefined;
        }

        const { shouldFetch, inputs } = SourceFetchConditionValidator.validateSourceFetchCondition(
            this,
            this.sourceFetchCondition
        );

        if (!shouldFetch) {
            return undefined;
        }

        const filter: Filter = inputs.reduce(
            (acc, input) => ({
                ...acc,
                [`${input.id}`]: input.value
            }),
            {}
        );

        return filter;
    }
}
